import React, { useId, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  Button,
  TextField,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  SelectProps,
  MenuItem,
  ListSubheader,
  Stack,
} from '@mui/material';

import { GET_COUNTRIES } from './ListItemAddress.graphql';
import { Address } from '../interfaces';
import Loading from './LoadingSpinner';
import { useQuery } from '@apollo/client/react';

interface Props {
  address: Address;
  open: boolean;
  onClose(): void;
  updateAddress(input: Address): void;
}

export function UpdateAddressDialog({ open, address, onClose, updateAddress }: Props) {
  const [newAddress, setNewAddress] = useState({ ...address });

  const handleChange = (event) => {
    let { id, value } = event.target;

    let newAddress2 = { ...newAddress };
    newAddress2[id] = value;

    setNewAddress(newAddress2);
  };

  const handleSelectChange = (event) => {
    let { value } = event.target;
    setNewAddress({ ...newAddress, country: value });
  };

  const abort = () => {
    setNewAddress({ ...address });
    onClose();
  };

  const commit = () => {
    delete newAddress['__typename'];
    updateAddress(newAddress);
    onClose();
  };

  const labels = {
    address1: 'Adresse1',
    address2: 'Adresse2',
    postalCode: 'Postnummer',
    city: 'Poststed',
  };

  return (
    <Dialog open={open} onClose={abort} fullWidth maxWidth="md">
      <DialogTitle>Endre adresse</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {Object.keys(labels).map((key) => (
            <TextField
              key={key}
              id={key}
              label={labels[key]}
              fullWidth
              value={newAddress[key] ? newAddress[key] : ''}
              onChange={handleChange}
            />
          ))}
          <CountrySelect label="Land" fullWidth value={newAddress.country} onChange={handleSelectChange} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={abort}>Avbryt</Button>
        <Button variant="contained" color="primary" onClick={commit}>
          Endre adresse
        </Button>
      </DialogActions>
    </Dialog>
  );
}

interface CountrySelectProps {
  value: string | null | undefined;
  onChange: (event: SelectChangeEvent<string>, child: React.ReactNode) => void;
  label: string;
  fullWidth?: boolean;
  required?: boolean;
  SelectProps?: Partial<SelectProps<string>>;
}

export function CountrySelect({ value, onChange, label, fullWidth, required, SelectProps }: CountrySelectProps) {
  const labelId = useId();
  const selectId = useId();
  const { loading, data } = useQuery<{ countries: { mostUsed: string[]; all: string[] } }>(GET_COUNTRIES);
  const countries = data?.countries ?? { mostUsed: [], all: [] };

  return (
    <FormControl fullWidth={fullWidth} required={required} disabled={loading}>
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select
        labelId={labelId}
        id={selectId}
        label={label}
        value={value ?? ''}
        onChange={onChange}
        endAdornment={loading ? <Loading size={20} /> : undefined}
        {...SelectProps}>
        <ListSubheader disableSticky>
          Mest brukt
        </ListSubheader>
        {countries.mostUsed.map((x) => (
          <MenuItem key={`mu-${x}`} value={x}>
            {x}
          </MenuItem>
        ))}
        <ListSubheader disableSticky>
          Resten
        </ListSubheader>
        {countries.all.map((x) => (
          <MenuItem key={`all-${x}`} value={x}>
            {x}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
